import { InsuranceCheckProblem, InsuranceNoPrimaryReason } from "@medbillai/generated-graphql-types";
import { Body3, Button, Card, Display4, openMailto, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { analyticsService } from "../../../lib/analytics";
import type { EventTypes } from "../../../lib/analytics/types";
import { useBoolVariation } from "../../../lib/flag";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { shortDate } from "../format";
import type { CheckingWait, CoverageRead, PlanSummary } from "../read/readCoverage";
import { DetailsChecking, detailsFix, FixDetailsCard } from "./FixDetailsCard";
import { SetupChecking } from "./PlanSetupForm";
import { PlanRow, usePickPlan } from "./PlanSwitcher";
import { SetupCard } from "./SetupCard";
import { SetupIllustration } from "./SetupIllustration";
import type { PlanSetup } from "./usePlanSetup";
type NonPlanRead = Exclude<CoverageRead, {
  kind: "plan" | "uninsured";
}>;
type AddSource = EventTypes["CoverageAddInsuranceTapped"]["source"];
function useAddInsurance(setup: PlanSetup) {
  return (source: AddSource, plan?: PlanSummary | null) => {
    analyticsService.trackEvent("CoverageAddInsuranceTapped", {
      source
    });
    setup.openSheet(source === "checkDetails" ? "profile" : "form", plan ? {
      carrier: plan.payerName,
      payerId: plan.payerId,
      logoUrl: plan.logoUrl,
      memberIdRequired: false
    } : undefined);
  };
}

/** What the root shows with no plan to lead with, or under a failing plan's header. */
export function CoverageStateCard({
  read,
  setup
}: {
  read: NonPlanRead;
  setup: PlanSetup;
}) {
  const add = useAddInsurance(setup);
  const fixesInCard = useBoolVariation(Flag.INSURANCE_MEMBER_ID_ASK, false);
  const recheckingDetails = useCoveragePlanStore(state => state.checkingSince !== undefined);
  switch (read.kind) {
    case "setup":
      return <SetupCard setup={setup} missing={read.missing} />;
    case "checking":
      // The setup run's own poll lands here; its card stays until it ends.
      return setup.checking ? <SetupCard setup={setup} missing={[]} /> : <Card variant="raised" size="large" padding="$sp2">
          <SetupIllustration />
          <SetupChecking {...checkingCopy[read.wait]} />
          {read.wait === "stuck" ? <Button variant="secondary" marginTop="$sp1" onPress={() => openMailto("mailto:support@grantedhealth.com")}>
              Contact support
            </Button> : null}
        </Card>;
    case "noPlan":
      {
        const {
          title,
          body,
          action
        } = noPlanCopy(read);
        return <Card variant="raised" size="large" padding="$sp2" gap="$sp1.5">
          <Display4 color="$textDefault">{title}</Display4>
          <Body3 color="$textSubtle">{body}</Body3>
          <PlansOnFile plans={read.plans} />
          {action === "checkDetails" ? <YStack gap="$sp0.5">
              <Button variant="secondary" onPress={() => add("checkDetails")}>
                Verify details
              </Button>
              <Button variant="tertiary" onPress={() => add("noPlan")}>
                Try another plan
              </Button>
            </YStack> : <Button variant="secondary" fillWidth={false} onPress={() => add(action)}>
              Add insurance
            </Button>}
        </Card>;
      }
    case "failing":
      {
        // A saved fix can re-key the plan or change its problem before the
        // re-check answers, so the wait is shown whatever card the read picks.
        if (recheckingDetails && read.plan) {
          return <DetailsChecking payerName={read.plan.payerName} />;
        }
        const fix = detailsFix(read.problem);
        return fixesInCard && read.plan && read.plan.confirmedAt === null && fix ? <FixDetailsCard plan={read.plan} fix={fix} onTryAnotherPlan={() => add("couldntConfirm")} /> : <Card variant="raised" size="large" padding="$sp2" gap="$sp1.5">
          <Display4 color="$textDefault">
            We couldn’t confirm your plan
          </Display4>
          <Body3 color="$textSubtle">
            {problemCopy(read.problem, read.plan?.payerName ?? null) ?? (read.lastConfirmedAt ? `Last confirmed ${shortDate(read.lastConfirmedAt)}` : "Check the details you gave, or try another plan.")}
          </Body3>
          {/* With a plan, the header's switcher reaches the others. */}
          {read.plan || read.otherPlans.length === 0 ? null : <PlansOnFile plans={read.otherPlans} />}
          <YStack gap="$sp0.5">
            {detailsNotAtFault(read.problem) ? null : <Button variant="secondary" onPress={() => add("checkDetails", read.plan)}>
                Verify details
              </Button>}
            <Button variant={detailsNotAtFault(read.problem) ? "secondary" : "tertiary"} onPress={() => add("couldntConfirm")}>
              Try another plan
            </Button>
          </YStack>
        </Card>;
      }
  }
}
const checkingCopy: Record<CheckingWait, {
  title?: string;
  detail: string;
}> = {
  usual: {
    detail: "This usually takes a few seconds."
  },
  long: {
    title: "Still checking your plan…",
    detail: "This is taking longer than usual. You can leave this page; we’ll keep checking."
  },
  stuck: {
    title: "Still checking your plan…",
    detail: "This is taking much longer than it should."
  }
};
const noPlanCopy = ({
  reason,
  memberIdKnown
}: Extract<NonPlanRead, {
  kind: "noPlan";
}>): {
  title: string;
  body: string;
  action: AddSource;
} => {
  switch (reason) {
    case InsuranceNoPrimaryReason.PLAN_ENDED:
      return {
        title: "Your plan ended",
        body: "Add your current insurance to see where you stand.",
        action: "noPlan"
      };
    case InsuranceNoPrimaryReason.NOT_MEDICAL:
      return {
        title: "We don’t see a medical plan for you",
        body: "Coverage follows your medical plan. Add yours, or pick one below.",
        action: "noPlan"
      };
    case InsuranceNoPrimaryReason.AMBIGUOUS:
      return {
        title: "We found more than one plan",
        body: "Pick the plan you use below to see where you stand.",
        action: "noPlan"
      };
    default:
      return {
        title: "We couldn’t match a plan to you",
        body: memberIdKnown ? "Check that your full legal name and date of birth are right." : "Check that your full legal name and date of birth are right, and add your member ID.",
        action: "checkDetails"
      };
  }
};
function PlansOnFile({
  plans
}: {
  plans: PlanSummary[];
}) {
  const pick = usePickPlan();
  return (
    // ListItem pads its own rows; line them up with the card's text.
    <YStack marginHorizontal="$-sp2" data-sentry-element="YStack" data-sentry-component="PlansOnFile" data-sentry-source-file="CoverageStateCard.tsx">
      {plans.map(plan => <PlanRow key={plan.membershipId} plan={plan} selected={false} onPress={() => pick(plan)} />)}
    </YStack>
  );
}

/**
 * The user's details aren't what's wrong: the insurer can't be checked, or
 * the payer rejected our request.
 */
const detailsNotAtFault = (problem: InsuranceCheckProblem | null) => problem === InsuranceCheckProblem.INSURER_UNAVAILABLE || problem === InsuranceCheckProblem.INSURER_NOT_SUPPORTED || problem === InsuranceCheckProblem.REQUEST_REJECTED;

/** Copy for a problem; null when it names the payer and no plan names one. */
const problemCopy = (problem: InsuranceCheckProblem | null, payer: string | null): string | null => {
  if (problem === InsuranceCheckProblem.REQUEST_REJECTED) {
    return `We couldn’t check your plan${payer === null ? "" : ` with ${payer}`}. That’s a problem on our end, not your details.`;
  }
  if (payer === null) {
    return null;
  }
  switch (problem) {
    case InsuranceCheckProblem.MEMBER_NOT_FOUND:
      return `${payer} couldn’t find a plan with the details you gave. Check them, or try another plan.`;
    case InsuranceCheckProblem.DATE_OF_BIRTH_MISMATCH:
      return `${payer} has a different date of birth on file for the policyholder. Check the policyholder’s date of birth.`;
    case InsuranceCheckProblem.INSURER_UNAVAILABLE:
      return `${payer} isn’t answering right now. Try again later.`;
    case InsuranceCheckProblem.INSURER_NOT_SUPPORTED:
      return notSupportedCopy(payer);
    default:
      return null;
  }
};
export const notSupportedCopy = (payer: string) => `${payer} doesn’t share plan details with us yet.`;